import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { useAuth } from '../../hooks/useAuth';
import usePiBrowserDetection from '../../hooks/usePiBrowserDetection';
import donationService from '../../services/donationService';
import fiatDonationService from '../../services/fiatDonationService';
import toast from 'react-hot-toast';
import { useFlutterwave, closePaymentModal } from 'flutterwave-react-v3';
import { getCurrencySymbol } from '../../constants/index.js';

const DonationModal = ({ project, isOpen, onClose, onSuccess }) => {
  const { user, isAuthenticated } = useAuth();
  const { isPiBrowser } = usePiBrowserDetection();
  const [amount, setAmount] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('PI');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState('idle');
  const paymentCompletedRef = useRef(false);

  // Available currencies (hide NGN in Pi Browser since Pi users don't have email)
  const currencies = [
    { code: 'PI', name: 'Pi', symbol: 'π', min: 1, max: 10000 },
    ...(!isPiBrowser ? [{ code: 'NGN', name: 'Nigerian Naira', symbol: '₦', min: 1000, max: 5000000 }] : [])
  ];

  const currentCurrency = currencies.find(c => c.code === selectedCurrency);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setAmount('');
      setSelectedCurrency('PI');
      setIsProcessing(false);
      setPaymentStatus('idle');
      paymentCompletedRef.current = false;
    }
  }, [isOpen]);

  // Calculate commission breakdown
  const getCommissionBreakdown = () => {
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) return null;

    if (selectedCurrency === 'NGN') {
      return fiatDonationService.calculateCommission(numAmount);
    }
    return donationService.calculateCommission(numAmount);
  };

  // Validate amount
  const validateAmount = () => {
    const numAmount = parseFloat(amount);

    if (!numAmount || numAmount <= 0) {
      return { valid: false, error: 'Please enter a valid amount' };
    }

    if (selectedCurrency === 'NGN') {
      return fiatDonationService.validateDonationAmount(numAmount);
    }
    return donationService.validateDonationAmount(numAmount);
  };

  // Handle amount input
  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  // Handle donation
  const handleDonate = async () => {
    if (!isAuthenticated) {
      toast.error('Please login to donate');
      return;
    }

    const validation = validateAmount();
    if (!validation.valid) {
      toast.error(validation.error);
      return;
    }

    const numAmount = parseFloat(amount);

    if (selectedCurrency === 'NGN') {
      return handleFiatDonation(numAmount);
    }
    return handlePiDonation(numAmount);
  };

  // Handle Pi donation
  const handlePiDonation = async (numAmount) => {
    setIsProcessing(true);
    setPaymentStatus('processing');

    try {
      const validationResult = await donationService.createDonation(
        project.id,
        numAmount,
        `Donation to ${project.title}`
      );

      const donationData = validationResult.data;

      const paymentData = {
        amount: numAmount,
        memo: `Donation to ${project.title}`,
        metadata: {
          project_id: project.id,
          project_slug: project.slug,
          donor_id: user.id,
          type: 'donation'
        }
      };

      await donationService.handlePiPayment(paymentData, {
        onApproval: async (piPaymentId) => {

          toast.success('Payment approved! Please complete the transaction.');
        },

        onCompletion: (result) => {
          setPaymentStatus('success');
          setIsProcessing(false);

          if (result.data.badgeUpgraded) {
            toast.success(
              <div>
                <p>Thank you for your π{numAmount} donation!</p>
                <p className="font-bold mt-2">
                  🎉 Congratulations! You've earned the {result.data.newBadge} badge!
                </p>
              </div>,
              { duration: 6000 }
            );
          } else {
            toast.success(`Thank you for your π${numAmount} donation!`);
          }

          if (onSuccess) onSuccess(result);

          setTimeout(() => {
            onClose();
          }, 2000);
        },

        onCancel: (piPaymentId) => {
          //console.log('Payment cancelled:', piPaymentId);
          setIsProcessing(false);
          setPaymentStatus('idle');
          toast.info('Donation cancelled');
        },

        onError: (error) => {
          console.error('Payment error:', error);
          setIsProcessing(false);
          setPaymentStatus('error');
          toast.error(error.message || 'Payment failed. Please try again.');
        }
      }, donationData);
    } catch (error) {
      console.error('Pi donation error:', error);
      setIsProcessing(false);
      setPaymentStatus('error');
      toast.error(error.message || 'Failed to process donation');
    }
  };

  const [flwConfig, setFlwConfig] = useState(null);
  const handleFlutterwavePayment = useFlutterwave(flwConfig || {
    public_key: '',
    tx_ref: '',
    amount: 0,
    currency: 'NGN',
    payment_options: 'card,ussd,opay',
    customer: { email: '', name: '' },
    customizations: { title: '', description: '', logo: '/images/CrowBlock%20Logo.png' }
  });

  // Effect to trigger Flutterwave when config is ready
  useEffect(() => {
    if (flwConfig && isProcessing && selectedCurrency === 'NGN') {
      handleFlutterwavePayment({
        callback: (response) => {
          // Always close the Flutterwave popup first
          closePaymentModal();

          // Flutterwave may return different status values depending on payment method
          const isSuccessful = ['successful', 'completed', 'success'].includes(
            (response.status || '').toLowerCase()
          );

          if (isSuccessful) {
            paymentCompletedRef.current = true;
            setPaymentStatus('success');
            setIsProcessing(false);

            // Verify payment on backend to trigger DB updates
            fiatDonationService.verifyPayment(response.transaction_id)
              .then(() => {
                toast.success(`Thank you for your ₦${amount} donation!`);
                if (onSuccess) onSuccess({
                  status: response.status,
                  tx_ref: response.tx_ref,
                  transaction_id: response.transaction_id,
                  data: { badgeUpgraded: false }
                });
                setTimeout(() => onClose(), 2000);
              })
              .catch(() => {
                // Webhook will still handle it; show success to user
                toast.success(`Thank you for your ₦${amount} donation!`);
                if (onSuccess) onSuccess({
                  status: response.status,
                  tx_ref: response.tx_ref,
                  transaction_id: response.transaction_id,
                  data: { badgeUpgraded: false }
                });
                setTimeout(() => onClose(), 2000);
              });
          } else {
            setIsProcessing(false);
            setPaymentStatus('error');
            toast.error('Payment was not successful.');
          }
          setFlwConfig(null);
        },
        onClose: () => {
          if (!paymentCompletedRef.current) {
            // User closed checkout without completing payment
            setIsProcessing(false);
            setPaymentStatus('idle');
            toast('Payment window closed');
          }
          // If payment was already completed, keep the success state
          setFlwConfig(null);
        },
      });
    }
  }, [flwConfig, isProcessing, selectedCurrency, amount, onSuccess, onClose, handleFlutterwavePayment]);

  // Handle Fiat donation
  const handleFiatDonation = async (numAmount) => {
    setIsProcessing(true);
    setPaymentStatus('processing');

    try {
      const userEmail = user?.google_email;
      if (!userEmail) {
        toast.error('Email not found. Please login with Google.');
        setIsProcessing(false);
        setPaymentStatus('idle');
        return;
      }

      // 1. Initialize donation on backend
      const result = await fiatDonationService.initializePayment(
        project.id,
        numAmount,
        userEmail,
        {
          project_slug: project.slug,
          project_title: project.title,
          donorName: user?.full_name || user?.google_name || 'Anonymous Donor',
          paymentMethod: 'hosted_checkout'
        }
      );

      if (!result.success) {
        throw new Error(result.message || 'Back-end initialization failed');
      }

      // 2. Set config to trigger the useEffect above
      // Robust data extraction to handle any API response structure
      const donationResult = result.data?.donation || result.donation || {};
      const publicKey = result.data?.publicKey || result.publicKey;

      const config = fiatDonationService.getFlutterwaveConfig({
        amount: numAmount,
        email: userEmail,
        name: user?.full_name || user?.google_name || 'Anonymous Donor',
        reference: donationResult.reference,
        projectId: project.id,
        donationId: donationResult.id,
        publicKey: publicKey,
        paymentMethod: 'card,ussd,opay,account' // Let Flutterwave show all options
      });

      setFlwConfig(config);

    } catch (error) {
      console.error('Fiat donation error:', error);
      setIsProcessing(false);
      setPaymentStatus('error');
      toast.error(error.message || 'Failed to initialize payment');
    }
  };

  const breakdown = getCommissionBreakdown();

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="w-full max-w-lg mx-auto my-8"
          onClick={(e) => e.stopPropagation()}
        >
          <Card className="border-0 shadow-2xl bg-white/95 backdrop-blur-md">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-xl">
                  <div className="w-8 h-8 bg-primary-teal/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-primary-teal" />
                  </div>
                  Support Project
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={onClose}
                  className="h-8 w-8 rounded-full hover:bg-neutral-100"
                  disabled={isProcessing}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-sm text-neutral-600 mt-2">
                Support "{project.title}" with your donation
              </p>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Project Goal Info */}
              <div className="bg-gradient-to-br from-blue-50 to-primary-teal/10 border-2 border-primary-teal/30 rounded-xl p-4">
                <p className="text-sm text-neutral-700 mb-1">
                  <strong>Project Goal:</strong> {getCurrencySymbol(project.currency)}{parseFloat(project.funding_goal || 0).toLocaleString()}
                </p>
                <p className="text-xs text-neutral-600">
                  Raising funds in {project.currency === 'NGN' ? 'Nigerian Naira (₦)' : 'Pi (π)'}
                </p>
              </div>

              {/* Currency Selector */}
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex-1">
                  <label className="block text-sm font-medium mb-2">
                    Select Donation Currency
                  </label>
                  <select
                    value={selectedCurrency}
                    onChange={(e) => {
                      setSelectedCurrency(e.target.value);
                      setAmount(''); // Reset amount when currency changes
                    }}
                    disabled={isProcessing}
                    className="w-full px-4 py-3 border-2 border-neutral-200 rounded-xl focus:border-primary-teal focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed text-base"
                  >
                    {currencies.map(currency => (
                      <option key={currency.code} value={currency.code}>
                        {currency.name} ({currency.symbol})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Enter Donation Amount
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-lg text-neutral-500">
                    {currentCurrency?.symbol}
                  </span>
                  <input
                    type="text"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder={`Enter amount (min: ${currentCurrency?.symbol}${currentCurrency?.min.toLocaleString()})`}
                    disabled={isProcessing}
                    className="w-full pl-10 pr-4 py-3 border-2 border-neutral-200 rounded-xl focus:border-primary-teal focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed text-base"
                  />
                </div>
                <p className="text-xs text-neutral-500 mt-1">
                  Min: {currentCurrency?.symbol}{currentCurrency?.min.toLocaleString()} •
                  Max: {currentCurrency?.symbol}{currentCurrency?.max.toLocaleString()}
                </p>
              </div>

              {/* Donation Breakdown */}
              <AnimatePresence>
                {breakdown && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="bg-neutral-50 rounded-xl p-4"
                  >
                    <h3 className="font-semibold mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-primary-teal" />
                      Donation Breakdown
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Your Donation:</span>
                        <span className="font-bold">
                          {currentCurrency?.symbol}{breakdown.gross}
                        </span>
                      </div>
                      {selectedCurrency === 'NGN' && (
                        <div className="flex justify-between text-neutral-600">
                          <span>Flutterwave Fee:</span>
                          <span>{currentCurrency?.symbol}{breakdown.flutterwaveFee}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-neutral-600">
                        <span>To Project:</span>
                        <span>{currentCurrency?.symbol}{breakdown.net}</span>
                      </div>
                      <div className="flex justify-between text-neutral-600">
                        <span>Platform Fee (1.99%):</span>
                        <span>
                          {currentCurrency?.symbol}
                          {selectedCurrency === 'NGN' ? breakdown.platformFee : breakdown.commission}
                        </span>
                      </div>
                      <div className="border-t pt-2 flex justify-between font-bold">
                        <span>Total Payment:</span>
                        <span className="text-primary-teal">
                          {currentCurrency?.symbol}{breakdown.gross}
                        </span>
                      </div>
                    </div>
                    <p className="text-xs text-neutral-500 mt-2">
                      {selectedCurrency === 'NGN'
                        ? 'Fees include Flutterwave transaction fee + 1.99% platform fee'
                        : 'Platform fees support hunger relief initiatives'
                      }
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Payment Status */}
              <AnimatePresence>
                {paymentStatus === 'processing' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3"
                  >
                    <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                    <div>
                      <p className="font-medium text-blue-900">Processing Payment</p>
                      <p className="text-sm text-blue-700">
                        {selectedCurrency === 'PI'
                          ? 'Please complete the transaction in Pi Wallet'
                          : 'Redirecting to secure Flutterwave checkout...'
                        }
                      </p>
                    </div>
                  </motion.div>
                )}

                {paymentStatus === 'success' && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium text-green-900">Donation Successful!</p>
                      <p className="text-sm text-green-700">Thank you for your contribution</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={onClose}
                  disabled={isProcessing}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDonate}
                  disabled={!breakdown || isProcessing || paymentStatus === 'success'}
                  className="flex-1 bg-primary-teal hover:bg-primary-teal/90"
                >
                  {isProcessing ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Donate {currentCurrency?.symbol}{breakdown?.gross || '0'}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DonationModal;
